import { useState } from 'react';
import { useI18n } from '@/hooks/useI18n';
import { useToast } from '@/hooks/useToast';
import { adminUsersApi, ownerApi } from '@/features/workspace/api';
import { RoleCategoryEditor } from './RoleCategoryEditor';
import { OwnerAccessModal } from './OwnerAccessModal';
import type {
  AccessRequestAdmin,
  AdminUserCatalog,
  BulkRowIn,
  BulkRowResult,
  CategoryAssignment,
} from '@/features/owner/adminUsersTypes';

function checkNoteClass(code: string | null, canInvite: boolean): string {
  if (code === 'MAILBOX_UNVERIFIED') return ' warn';
  if (canInvite) return ' good';
  return ' bad';
}

function bulkStatusLabel(status: string, t: (key: import('@/i18n').TranslationKey) => string): string {
  if (status === 'READY') return t('admin.users.bulk.READY');
  if (status === 'EXISTING') return t('admin.users.bulk.EXISTING');
  if (status === 'CONFLICT') return t('admin.users.bulk.CONFLICT');
  if (status === 'ERROR') return t('admin.users.bulk.ERROR');
  return status;
}

function bulkStatusClass(status: string): string {
  if (status === 'READY') return ' mint';
  if (status === 'EXISTING') return '';
  if (status === 'CONFLICT' || status === 'ERROR') return ' red';
  return ' gold';
}

interface AddUserModalProps {
  catalog: AdminUserCatalog;
  onClose: () => void;
  onCreated: () => void;
}

export const AddUserModal = ({ catalog, onClose, onCreated }: AddUserModalProps) => {
  const { t, locale } = useI18n();
  const { notify } = useToast();
  const [email, setEmail] = useState('');
  const [checking, setChecking] = useState(false);
  const [canInvite, setCanInvite] = useState(false);
  const [checkCode, setCheckCode] = useState<string | null>(null);
  const [existingUserId, setExistingUserId] = useState<string | null>(null);
  const [checkDetail, setCheckDetail] = useState<string | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [categories, setCategories] = useState<CategoryAssignment[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const supervised = categories.find((item) => item.category_code === 'SUPERVISED_STUDENT');
  const needsEducation =
    roles.includes('STUDENT') && !String(supervised?.metadata.education_level ?? '').trim();

  const handleCheck = async () => {
    if (!email.trim()) return;
    setChecking(true);
    try {
      const result = await ownerApi.checkInvite(email.trim(), locale);
      setCanInvite(result.can_invite);
      setCheckCode(result.code);
      setExistingUserId(result.user_id);
      setCheckDetail(result.detail);
    } catch (error) {
      setCanInvite(false);
      setCheckCode(null);
      setExistingUserId(null);
      setCheckDetail(error instanceof Error ? error.message : t('common.error'));
    } finally {
      setChecking(false);
    }
  };

  const handleResend = async () => {
    if (!existingUserId) return;
    setSubmitting(true);
    try {
      const invitation = await adminUsersApi.resendInvitation(existingUserId, locale);
      if (invitation.email_delivery === 'sent') {
        notify(t('admin.users.toast.resent'), 'success');
      } else if (invitation.email_delivery === 'not_delivered') {
        notify(invitation.delivery_detail || t('admin.users.toast.notDelivered'), 'error');
      } else if (invitation.email_delivery === 'failed') {
        notify(invitation.delivery_detail || t('admin.users.toast.emailFailed'), 'error');
      } else {
        notify(t('admin.users.toast.emailNotConfigured'), 'info');
      }
      onCreated();
    } catch (error) {
      notify(error instanceof Error ? error.message : t('common.error'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!canInvite || needsEducation || roles.length === 0) return;
    setSubmitting(true);
    try {
      const created = await adminUsersApi.create({
        email: email.trim(),
        first_name: '',
        last_name: '',
        position: null,
        department: null,
        roles,
        categories,
        locale,
      });
      if (created.invitation.email_delivery === 'sent') {
        notify(t('admin.users.toast.created'), 'success');
      } else if (created.invitation.email_delivery === 'not_delivered') {
        notify(created.invitation.delivery_detail || t('admin.users.toast.notDelivered'), 'error');
      } else if (created.invitation.email_delivery === 'failed') {
        notify(created.invitation.delivery_detail || t('admin.users.toast.createdEmailFailed'), 'error');
      } else {
        notify(t('admin.users.toast.createdEmailQueued'), 'info');
      }
      onCreated();
      onClose();
    } catch (error) {
      notify(error instanceof Error ? error.message : t('common.error'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OwnerAccessModal
      title={t('admin.users.modal.add')}
      onClose={onClose}
      wide
      footer={
        canInvite ? (
          <>
            <button type="button" className="owner-access-btn" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="owner-access-btn gold"
              disabled={submitting || needsEducation || roles.length === 0}
              onClick={() => void handleSubmit()}
            >
              {t('admin.users.add.submit')}
            </button>
          </>
        ) : null
      }
    >
      <label className="owner-access-field full">
        <span>{t('admin.users.add.email')}</span>
        <input
          type="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            setCanInvite(false);
            setCheckCode(null);
            setExistingUserId(null);
            setCheckDetail(null);
          }}
        />
      </label>
      <div className="owner-access-modal-actions" style={{ justifyContent: 'flex-start' }}>
        <button
          type="button"
          className="owner-access-btn"
          disabled={checking || !email.trim()}
          onClick={() => void handleCheck()}
        >
          {t('admin.users.add.checkEmail')}
        </button>
      </div>
      {checkDetail ? (
        <div className={`owner-access-note${checkNoteClass(checkCode, canInvite)}`}>{checkDetail}</div>
      ) : null}
      {checkCode === 'INVITATION_PENDING' && existingUserId ? (
        <button
          type="button"
          className="owner-access-btn"
          disabled={submitting}
          onClick={() => void handleResend()}
        >
          {t('admin.users.invitation.resend')}
        </button>
      ) : null}
      {needsEducation ? (
        <div className="owner-access-note warn">{t('admin.users.add.educationRequired')}</div>
      ) : null}

      {canInvite ? (
        <RoleCategoryEditor
          roles={catalog.roles}
          categories={catalog.categories}
          selectedRoles={roles}
          selectedCategories={categories}
          onRolesChange={setRoles}
          onCategoriesChange={setCategories}
          accessOnly
        />
      ) : null}
    </OwnerAccessModal>
  );
};

interface BulkAddModalProps {
  catalog: AdminUserCatalog;
  onClose: () => void;
  onCreated: () => void;
}

let bulkRowSeq = 0;
const emptyRow = (): BulkRowIn => ({
  row_index: bulkRowSeq++,
  first_name: '',
  last_name: '',
  email: '',
  roles: [],
  categories: [],
});

export const BulkAddModal = ({ catalog, onClose, onCreated }: BulkAddModalProps) => {
  const { t, locale } = useI18n();
  const { notify } = useToast();
  const [rows, setRows] = useState<BulkRowIn[]>([emptyRow()]);
  const [results, setResults] = useState<BulkRowResult[] | null>(null);
  const [busy, setBusy] = useState(false);

  const updateRow = (index: number, patch: Partial<BulkRowIn>) => {
    setRows((current) => current.map((row) => (row.row_index === index ? { ...row, ...patch } : row)));
    setResults(null);
  };

  const addRow = () => setRows((current) => [...current, emptyRow()]);
  const removeRow = (index: number) =>
    setRows((current) => current.filter((row) => row.row_index !== index));

  const precheck = async () => {
    setBusy(true);
    try {
      const result = await adminUsersApi.bulkPrecheck(rows, locale);
      setResults(result.rows);
    } catch (error) {
      notify(error instanceof Error ? error.message : t('common.error'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const result = await adminUsersApi.bulkCommit(rows, locale);
      setResults(result.rows);
      notify(t('admin.users.bulk.committed', { count: result.created_count }), 'success');
      onCreated();
    } catch (error) {
      notify(error instanceof Error ? error.message : t('common.error'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const resultFor = (index: number) => results?.find((row) => row.row_index === index);

  return (
    <OwnerAccessModal
      title={t('admin.users.modal.bulk')}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="owner-access-btn" disabled={busy} onClick={() => void precheck()}>
            {t('admin.users.bulk.precheck')}
          </button>
          <button
            type="button"
            className="owner-access-btn gold"
            disabled={busy || !results}
            onClick={() => void commit()}
          >
            {t('admin.users.bulk.commit')}
          </button>
        </>
      }
    >
      <div className="owner-access-bulk-preview">
        <table className="owner-access-bulk-table">
          <thead>
            <tr>
              <th>{t('admin.users.field.firstName')}</th>
              <th>{t('admin.users.field.lastName')}</th>
              <th>Электронная почта</th>
              <th>{t('admin.users.field.roles')}</th>
              <th>{t('admin.users.field.categories')}</th>
              <th>{t('admin.users.bulk.status')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const result = resultFor(row.row_index);
              return (
                <tr key={row.row_index}>
                  <td>
                    <input
                      value={row.first_name}
                      onChange={(event) => updateRow(row.row_index, { first_name: event.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      value={row.last_name}
                      onChange={(event) => updateRow(row.row_index, { last_name: event.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      value={row.email}
                      onChange={(event) => updateRow(row.row_index, { email: event.target.value })}
                    />
                  </td>
                  <td>
                    <select
                      multiple
                      value={row.roles}
                      onChange={(event) =>
                        updateRow(row.row_index, {
                          roles: Array.from(event.target.selectedOptions).map((o) => o.value),
                        })
                      }
                      style={{ minWidth: 130 }}
                    >
                      {catalog.roles.map((role) => (
                        <option key={role.code} value={role.code}>
                          {role.label[locale]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      value={row.categories[0]?.category_code ?? ''}
                      onChange={(event) =>
                        updateRow(row.row_index, {
                          categories: event.target.value
                            ? [{ category_code: event.target.value, metadata: {} }]
                            : [],
                        })
                      }
                    >
                      <option value="">—</option>
                      {catalog.categories.map((category) => (
                        <option key={category.code} value={category.code}>
                          {category.label[locale]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {result ? (
                      <span className={`owner-access-chip${bulkStatusClass(result.status)}`}>
                        {bulkStatusLabel(result.status, t)}
                      </span>
                    ) : (
                      '—'
                    )}
                    {result?.reason ? (
                      <div className="owner-access-note" style={{ marginTop: 4 }}>
                        {result.reason}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="owner-access-btn"
                      onClick={() => removeRow(row.row_index)}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <button
        type="button"
        className="owner-access-btn"
        onClick={addRow}
        style={{ justifySelf: 'start' }}
      >
        + {t('admin.users.bulk.addRow')}
      </button>
    </OwnerAccessModal>
  );
};

interface AccessApproveModalProps {
  request: AccessRequestAdmin;
  catalog: AdminUserCatalog;
  onClose: () => void;
  onDecided: () => void;
}

export const AccessApproveModal = ({
  request,
  catalog,
  onClose,
  onDecided,
}: AccessApproveModalProps) => {
  const { t, locale } = useI18n();
  const { notify } = useToast();
  const [roles, setRoles] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const handleApprove = async () => {
    setBusy(true);
    try {
      const result = await adminUsersApi.approveAccess(request.id, roles, locale);
      notify(
        t(result.invitation.email_delivery === 'sent'
          ? 'admin.users.toast.accessApproved' : 'admin.users.toast.approvedUnsent'),
        result.invitation.email_delivery === 'sent' ? 'success' : 'error',
      );
      onDecided();
      onClose();
    } catch (error) {
      notify(error instanceof Error ? error.message : t('common.error'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <OwnerAccessModal
      title={t('admin.users.modal.approve')}
      subtitle={`${request.full_name} · ${request.email} · ${
        catalog.categories.find((c) => c.code === request.category_code)?.label[locale] ??
        request.category_code
      }`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="owner-access-btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="owner-access-btn gold"
            disabled={busy}
            onClick={() => void handleApprove()}
          >
            {t('admin.users.modal.approve')}
          </button>
        </>
      }
    >
      {request.comment && (
        <p style={{ marginBottom: '1rem', opacity: 0.8, fontStyle: 'italic', fontSize: '0.875rem' }}>
          💬 {request.comment}
        </p>
      )}
      <div className="owner-access-rolegrid">
        {catalog.roles.map((role) => (
          <label key={role.code} className="owner-access-roleopt">
            <input
              type="checkbox"
              checked={roles.includes(role.code)}
              onChange={() =>
                setRoles((current) =>
                  current.includes(role.code)
                    ? current.filter((item) => item !== role.code)
                    : [...current, role.code],
                )
              }
            />
            <span>{role.label[locale]}</span>
          </label>
        ))}
      </div>
    </OwnerAccessModal>
  );
};

interface AccessRejectModalProps {
  request: AccessRequestAdmin;
  onClose: () => void;
  onDecided: () => void;
}

export const AccessRejectModal = ({ request, onClose, onDecided }: AccessRejectModalProps) => {
  const { t } = useI18n();
  const { notify } = useToast();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const handleReject = async () => {
    setBusy(true);
    try {
      await adminUsersApi.rejectAccess(request.id, note || undefined);
      notify(t('admin.users.toast.accessRejected'), 'success');
      onDecided();
      onClose();
    } catch (error) {
      notify(error instanceof Error ? error.message : t('common.error'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <OwnerAccessModal
      title={t('admin.users.modal.reject')}
      subtitle={`${request.full_name} · ${request.email}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="owner-access-btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="owner-access-btn danger"
            disabled={busy}
            onClick={() => void handleReject()}
          >
            {t('admin.users.modal.reject')}
          </button>
        </>
      }
    >
      <label className="owner-access-field full">
        <span>{t('admin.users.status.reason')}</span>
        <textarea value={note} onChange={(event) => setNote(event.target.value)} />
      </label>
    </OwnerAccessModal>
  );
};
